/**
 * Swipe navigation between materials within a single sub-location on the cycle
 * count session (live count) screen (#30647). A left swipe advances to the next
 * material, a right swipe steps back to the previous one, following the ordered
 * material list for the current sub-location. These helpers are pure so the
 * component keeps only the gesture wiring and the save-on-swipe orchestration.
 */

/** Which way the counter is paging: 'next' = swipe-left, 'previous' = swipe-right. */
export type SwipeNavDirection = 'next' | 'previous';

/**
 * Resolves the target material index for a swipe within a sub-location (#30647).
 * Order follows the caller's ordered item list (the counting screen passes
 * `filteredItems()`). Returns the target index, or null when the swipe should do
 * nothing:
 *  - the sub-location has fewer than 2 materials (nothing to page through),
 *  - the current index is not a valid position in the list (e.g. findIndex
 *    returned -1 for an absent selection, or a stale index off the end), or
 *  - the move would run off either end — paging stops at the first/last
 *    material with no wrap-around.
 */
export function resolveSwipeTargetIndex(
  currentIndex: number,
  itemCount: number,
  direction: SwipeNavDirection
): number | null {
  if (itemCount < 2) return null;
  if (!Number.isInteger(currentIndex)) return null;
  if (currentIndex < 0 || currentIndex >= itemCount) return null;
  const target = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
  if (target < 0 || target >= itemCount) return null;
  return target;
}

/**
 * Position indicator text for the current material within its sub-location
 * (#30647), e.g. "2 of 5", so the counter knows where they are while paging.
 * The index is 0-based and the output is 1-based. Returns an empty string when
 * there is no meaningful position — an empty list, or a current index outside
 * the list — so the caller can render nothing rather than "0 of 5"/"1 of 0".
 */
export function formatSubLocationPosition(
  currentIndex: number,
  itemCount: number
): string {
  if (itemCount <= 0) return '';
  if (currentIndex < 0 || currentIndex >= itemCount) return '';
  return `${currentIndex + 1} of ${itemCount}`;
}

/**
 * Enhanced pager label for the material-entry screen (#31279). Rewords the bare
 * "2 of 5" position to "Item 2 of 5" and, when a bin/page label is supplied,
 * prefixes it as "A1.02 · Item 2 of 5" so a counter mid-swipe sees BOTH their
 * position and which sub-location they are in. A blank or whitespace-only bin
 * label is dropped (no orphaned separator). Returns an empty string whenever
 * there is no meaningful position (empty list or out-of-range index), so the
 * component renders no pager rather than "Item 0 of 5".
 */
export function formatItemPagerLabel(
  currentIndex: number,
  itemCount: number,
  binLabel?: string | null
): string {
  const position = formatSubLocationPosition(currentIndex, itemCount);
  if (position === '') return '';
  const item = `Item ${position}`;
  const bin = (binLabel ?? '').trim();
  return bin ? `${bin} · ${item}` : item;
}

/**
 * Count indicator for the bin's material list (#31279), e.g. "5 items" / "1
 * item", so a counter knows how many materials the current sub-location holds
 * before drilling in. Returns an empty string for an empty (or filtered-out)
 * list so no "0 items" chip is shown.
 */
export function formatBinItemCountLabel(count: number): string {
  if (count <= 0) return '';
  return count === 1 ? '1 item' : `${count} items`;
}
